import React from 'react';
import Loading from 'src/components/Loading/Loading';
import {
    RowWrapper,
    UploadWrapper,
    LabelButton,
    UploadButton,
    InputFile,
    UploadMessage,
    LinkCssLeft,
} from 'src/components/CSVUpload/CSVUpload.style';
import { observer } from 'mobx-react-lite';
import { computed, observable, makeAutoObservable } from 'mobx';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { AppState, useAppStateContext } from 'src/AppState/AppState';

const MESSAGE_SHOW_TIME = 30000;
const MAPPED_SIMPLES_NAME_FILES = [
    'events.csv',
    'markets.csv',
    'selections.csv',
    'participants.csv',
    'deposits.csv',
    'workbook.xlsx',
];

class MessageTimeout {
    @observable public show: boolean;

    public constructor() {
        this.show = true;

        setTimeout(() => {
            this.show = false;
        }, MESSAGE_SHOW_TIME);
        makeAutoObservable(this);
    }
}

class State {
    @observable.ref public message: null | MessageTimeout = null;
    @observable public requestInProgress: boolean = false;
    @observable public file: File | null = null;

    public constructor(private readonly appState: AppState) {
        makeAutoObservable(this);
    }

    @computed public get fileName(): string {
        if (this.file === null) {
            return '';
        }

        return this.file.name;
    }

    @computed public get showMessage(): boolean {
        const message = this.message;

        if (message === null) {
            return false;
        }

        return message.show;
    }

    public onSelectFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const target = event.target;

        if (target instanceof HTMLInputElement) {
            if (target.files !== null) {
                const file = target.files[0];

                if (file !== undefined) {
                    this.file = file;
                    return;
                }
            }
        }

        console.error('Problem receiving a file', event);
    };

    public upload = async (): Promise<void> => {
        if (this.file === null) {
            console.error('Upload - problem with uploading a file to the server, no file selected');
        } else {
            this.message = null;
            this.requestInProgress = true;

            try {
                const response = await this.appState.trpcClient.csvUpload(this.file);

                if (response.type === 'ok') {
                    await timeout(500);

                    this.message = new MessageTimeout();
                    this.requestInProgress = false;
                } else {
                    const message = `Error while uploading csv file to server: status: ${response.status} body:${response.body}`;
                    this.appState.reduxAction.addError(message);
                    this.requestInProgress = false;
                }
            } catch (error) {
                console.error(error);
                this.requestInProgress = false;
            }
        }
    };
}

export const CSVUpload = observer(() => {
    const appState = useAppStateContext();
    const [state] = React.useState(() => new State(appState));

    const link_simples_file = MAPPED_SIMPLES_NAME_FILES.map((nameFile) => {
        const prefixURL = 'https://s3.eu-central-1.amazonaws.com/prod-import-service/';
        return (
            <li key={nameFile}>
                <a
                    href={prefixURL + nameFile}
                    target='_blank'
                    rel='noreferrer'
                >
                    {nameFile}
                </a>
            </li>
        );
    });

    const elementWrapperClassName = `element required hasLabel ${state.showMessage ? 'valid' : ''}`;

    return (
        <form
            method='POST'
            className='form'
        >
            <div className='csvUpload'>
                <RowWrapper>
                    <UploadWrapper>
                        <div className='error-message-below'>
                            <div
                                className={elementWrapperClassName}
                                data-test='upload-file'
                            >
                                <input
                                    type='text'
                                    disabled={true}
                                    name='name'
                                    value={state.fileName}
                                />
                                <label>Upload file</label>
                            </div>
                        </div>
                    </UploadWrapper>

                    <LabelButton className='btn'>
                        Select csv
                        <InputFile
                            type='file'
                            accept='.csv,.xlsx'
                            onChange={state.onSelectFile}
                        />
                    </LabelButton>
                    <UploadButton
                        onClick={state.upload}
                        type='button'
                        className='btn'
                    >
                        Upload csv
                    </UploadButton>
                </RowWrapper>

                <div className='row'>
                    <LinkCssLeft className='col-1'>Sample files:</LinkCssLeft>
                    <div className='col-1'>
                        <ul>{link_simples_file}</ul>
                    </div>
                </div>

                {state.requestInProgress ? <Loading active={true} /> : null}

                {state.showMessage ? (
                    <UploadMessage className='row'>
                        <div className='col-12'>
                            <div className='succes'>Upload pending. Please check your email for any errors.</div>
                        </div>
                    </UploadMessage>
                ) : null}
            </div>
        </form>
    );
});
